/**
 * Product / funnel signals - gtag events only (Sentry removed).
 */
const DEBUG_TELEMETRY_KEY = "expirydesk:telemetry:events";

type TelemetryEntry = {
  name: string;
  params: Record<string, string>;
  at: string;
};

function rememberTelemetryEvent(name: string, params: Record<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    const existing = window.sessionStorage.getItem(DEBUG_TELEMETRY_KEY);
    const parsed = existing ? (JSON.parse(existing) as TelemetryEntry[]) : [];
    const next: TelemetryEntry[] = [
      {
        name,
        params,
        at: new Date().toISOString(),
      },
      ...(Array.isArray(parsed) ? parsed : []),
    ].slice(0, 20);
    window.sessionStorage.setItem(DEBUG_TELEMETRY_KEY, JSON.stringify(next));
  } catch {
    /* non-blocking */
  }
}

function gtagEvent(name: string, params: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  rememberTelemetryEvent(name, params);
  const g = (window as Window & { gtag?: (...a: unknown[]) => void }).gtag;
  if (typeof g === 'function') g('event', name, params);
}

export function trackOAuthRedirectStarted(provider: 'google'): void {
  gtagEvent('oauth_redirect_started', { method: provider });
}

export function trackOAuthTermsRecorded(provider: 'google' | 'apple'): void {
  gtagEvent('oauth_terms_recorded', { method: provider });
}

export function trackUpgradePromptShown(feature: string, variant: string, requiredPlan: string): void {
  gtagEvent("upgrade_prompt_shown", {
    feature,
    variant,
    required_plan: requiredPlan,
  });
}

export function trackUpgradePromptClicked(feature: string, variant: string, requiredPlan: string): void {
  gtagEvent("upgrade_prompt_clicked", {
    feature,
    variant,
    required_plan: requiredPlan,
  });
}

export function trackUpgradePromptDismissed(feature: string, variant: string, requiredPlan: string): void {
  gtagEvent("upgrade_prompt_dismissed", {
    feature,
    variant,
    required_plan: requiredPlan,
  });
}

export function trackPricingCtaClicked(plan: string, location: string, sourceFeature: string): void {
  gtagEvent("pricing_cta_clicked", {
    plan,
    location,
    source_feature: sourceFeature || "direct",
  });
}

export function trackCheckoutStarted(plan: string, location: string, sourceFeature: string): void {
  gtagEvent("checkout_started", {
    plan,
    location,
    source_feature: sourceFeature || "direct",
  });
}

export function getRecentTelemetryEvents(): TelemetryEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(DEBUG_TELEMETRY_KEY);
    const parsed = raw ? (JSON.parse(raw) as TelemetryEntry[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearRecentTelemetryEvents(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(DEBUG_TELEMETRY_KEY);
  } catch {
    /* non-blocking */
  }
}
